import React, { useEffect, useState } from "react";
import type { Period } from "../types";
import "./ControlPanel.css";

type Props = {
  period: Period | null;
  onApply: (body: {
    period: string;
    income_cents: number;
    label?: string;
    start_at?: string;
    end_at?: string;
  }) => Promise<void>;
};

const PRESETS: { label: string; value: string }[] = [
  { label: "Weekly", value: "weekly" },
  { label: "Bi-weekly", value: "biweekly" },
  { label: "Monthly", value: "monthly" },
];

export default function ControlPanel({ period, onApply }: Props) {
  const [periodKind, setPeriodKind] = useState<string>("monthly");
  const [income, setIncome] = useState<string>("2000");
  const [label, setLabel] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (period) {
      setIncome((period.income_cents / 100).toFixed(2));
      setLabel(period.label);
    }
  }, [period]);

  const apply = async (kind: string) => {
    setError(null);
    setBusy(true);
    try {
      const dollars = parseFloat(income);
      if (!isFinite(dollars) || dollars <= 0) throw new Error("income must be > 0");
      const cents = Math.round(dollars * 100);
      await onApply({
        period: kind,
        income_cents: cents,
        label: label || undefined,
      });
      setPeriodKind(kind);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel-title">budget</div>
      <div className="control-row">
        <label className="control-label">
          <span>income</span>
          <div className="money-input">
            <span>$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              placeholder="2000.00"
            />
          </div>
        </label>
        <label className="control-label">
          <span>label (optional)</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. May groceries"
          />
        </label>
      </div>

      <div className="period-row">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            className={`period-btn ${periodKind === p.value ? "is-active" : ""}`}
            disabled={busy}
            onClick={() => apply(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error ? <div className="control-error">{error}</div> : null}
    </div>
  );
}
